'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Mail, ArrowLeft, AlertTriangle } from 'lucide-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { authClient } from '@/lib/auth/client';
import {
  requestResetSchema,
  setNewPasswordSchema,
  type RequestResetFormValues,
  type SetNewPasswordFormValues,
} from '@/features/auth/types/auth-schemas';
import { PasswordStrengthIndicator } from './password-strength-indicator';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

// ── Email sent success view ─────────────────────────────────────

function EmailSentView({ email }: { email: string }) {
  const t = useTranslations('auth.resetPassword');

  return (
    <div className="space-y-6 animate-scale-in">
      <div className="flex justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
          <Mail className="size-8 text-primary" />
        </div>
      </div>

      <div className="space-y-2 text-center">
        <h1 className="font-display text-2xl font-bold text-foreground">
          {t('successTitle')}
        </h1>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t('successDescription')}{' '}
          <span className="font-medium text-foreground">{email}</span>,{' '}
          {t('successDescription2')}
        </p>
        <p className="text-xs text-muted-foreground">{t('linkExpiry')}</p>
      </div>

      <div className="text-center">
        <Link
          href="/login"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          {t('backToLogin')}
        </Link>
      </div>
    </div>
  );
}

// ── Token error view ────────────────────────────────────────────

function TokenErrorView({ message }: { message: string }) {
  const t = useTranslations('auth.resetPassword');

  return (
    <div className="space-y-6 animate-scale-in">
      <div className="flex justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10">
          <AlertTriangle className="size-8 text-destructive" />
        </div>
      </div>

      <div className="space-y-2 text-center">
        <h1 className="font-display text-2xl font-bold text-foreground">
          {t('invalidLink')}
        </h1>
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>

      <Button asChild className="w-full h-11 font-semibold">
        <Link href="/reset-password">{t('requestNewLink')}</Link>
      </Button>
    </div>
  );
}

// ── Request reset form ──────────────────────────────────────────

function RequestResetForm() {
  const t = useTranslations('auth.resetPassword');
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const form = useForm<RequestResetFormValues>({
    resolver: zodResolver(requestResetSchema),
    defaultValues: { email: '' },
  });

  const isSubmitting = form.formState.isSubmitting;

  async function onSubmit(values: RequestResetFormValues) {
    try {
      // Always show success (don't reveal account existence)
      await authClient.requestPasswordReset({
        email: values.email,
        redirectTo: '/reset-password',
      });
      setSentEmail(values.email);
      setSent(true);
    } catch {
      // Still show success for security
      setSentEmail(values.email);
      setSent(true);
    }
  }

  if (sent) {
    return <EmailSentView email={sentEmail} />;
  }

  return (
    <div className="space-y-6 animate-scale-in">
      <div className="space-y-1.5">
        <h1 className="font-display text-2xl font-bold text-foreground">
          {t('title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('emailLabel')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t('emailPlaceholder')}
                    autoComplete="email"
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full h-11 gap-2 font-semibold"
            disabled={isSubmitting}
          >
            {isSubmitting && <Spinner className="size-4" />}
            {isSubmitting ? t('loading') : t('submitButton')}
          </Button>
        </form>
      </Form>

      <div className="text-center">
        <Link
          href="/login"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          {t('backToLogin')}
        </Link>
      </div>
    </div>
  );
}

// ── Set new password form ───────────────────────────────────────

function SetNewPasswordForm({ token }: { token: string }) {
  const t = useTranslations('auth.resetPassword');
  const router = useRouter();
  const [tokenError, setTokenError] = useState<string | null>(null);

  const form = useForm<SetNewPasswordFormValues>({
    resolver: zodResolver(setNewPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const newPassword = form.watch('newPassword');
  const isSubmitting = form.formState.isSubmitting;

  async function onSubmit(values: SetNewPasswordFormValues) {
    setTokenError(null);
    try {
      const result = await authClient.resetPassword({
        newPassword: values.newPassword,
        token,
      });

      if (result.error) {
        const msg = result.error.message ?? '';
        if (
          msg.toLowerCase().includes('invalid') ||
          msg.toLowerCase().includes('expired') ||
          msg.toLowerCase().includes('token')
        ) {
          setTokenError(t('tokenError'));
          return;
        }
        toast.error(msg || t('connectionError'));
        return;
      }

      toast.success(t('resetSuccess'));
      router.push('/login');
    } catch {
      toast.error(t('connectionError'));
    }
  }

  if (tokenError) {
    return <TokenErrorView message={tokenError} />;
  }

  return (
    <div className="space-y-6 animate-scale-in">
      <div className="space-y-1.5">
        <h1 className="font-display text-2xl font-bold text-foreground">
          {t('newPasswordTitle')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('newPasswordSubtitle')}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('newPasswordLabel')}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t('newPasswordPlaceholder')}
                    autoComplete="new-password"
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <PasswordStrengthIndicator
                  password={newPassword}
                  showChecklist
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('confirmPasswordLabel')}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t('confirmPasswordPlaceholder')}
                    autoComplete="new-password"
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full h-11 gap-2 font-semibold"
            disabled={isSubmitting}
          >
            {isSubmitting && <Spinner className="size-4" />}
            {isSubmitting ? t('updating') : t('updateButton')}
          </Button>
        </form>
      </Form>
    </div>
  );
}

// ── Main export: ResetPasswordForm ──────────────────────────────

interface ResetPasswordFormProps {
  /** Token from the reset link URL (?token=...). If present, shows set-new-password form. */
  token?: string;
}

export function ResetPasswordForm({ token }: ResetPasswordFormProps) {
  if (token) {
    return <SetNewPasswordForm token={token} />;
  }
  return <RequestResetForm />;
}
